import { IconEyeglass } from '@tabler/icons-react';
import { BenefitType, SupplementalPlanForEnrollment } from '@zorro/clients';
import {
  MonolithMethodNames,
  UseMonolithQueryProps,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import { useEnrollmentAllowanceStore } from '../../EnrollmentAllowanceCard';
import { FullPageLoader } from '../../FullPageLoader';
import {
  PlanElectionForm,
  mapPlanElectionItemToEnrollmentAllowanceStorePlan,
  usePlanElectionForm,
} from '../../PlanElectionForm';
import {
  ManualSubmissionStep,
  ManualSubmissionStepSubmit,
  ManualSubmissionStepType,
} from '../ManualSubmissionStep';
import {
  exportSupplementalPlanList,
  getDefaultSupplementalPlanFormFields,
  getEmptySupplementalPlanFormFields,
  mapSupplementalPlanToPlanElectionItem,
} from '../ManualSubmissionSupplementalPlanStepUtils';
import { useBenefitAddToCart } from '../useBenefitAddToCart';

const benefitType = BenefitType.SUPPLEMENTAL_VISION;
const visionPlanMethod: MonolithMethodNames = 'visionControllerSearch';

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  onSubmit: () => void;
  isDon?: boolean;
};

export const ManualSubmissionVisionPlanStep = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  onSubmit: onSubmitProp,
  isDon = false,
}: Props) => {
  const setVisionPlans = useEnrollmentAllowanceStore(
    (state) => state.setVisionPlans
  );
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const visionBenefits = benefits?.visionBenefits;

  const { submitAddToCart } = useBenefitAddToCart({
    benefitType,
    onboardingPeriodId,
  });

  const {
    isSubmitting,
    planElectionForm,
    insured,
    insuredIdToNameMap,
    whoIsEnrolledInMultiplePlans,
    submittedWhoIsEnrolledOptions,
    onSubmit,
  } = usePlanElectionForm({
    employeeId,
    onboardingPeriodId,
    onSubmit: submitAddToCart,
    onPlansChange: (plans) => {
      const storePlans =
        mapPlanElectionItemToEnrollmentAllowanceStorePlan(plans);
      setVisionPlans(storePlans);
    },
    getDefaultFormFields: (allWhoIsEnrolled: string[] = []) => {
      return getDefaultSupplementalPlanFormFields(
        visionBenefits,
        allWhoIsEnrolled
      );
    },
  });

  if (!insured) {
    return <FullPageLoader />;
  }

  const handleStepSubmit: ManualSubmissionStepSubmit = async () => {
    if (await planElectionForm.trigger()) {
      onSubmitProp();
      return { navigate: await onSubmit() };
    }

    return { navigate: false };
  };

  const handleGetParams = (
    whoIsEnrolled: string[]
  ): UseMonolithQueryProps<typeof visionPlanMethod>['params'] => {
    return [onboardingPeriodId, { insuredIds: whoIsEnrolled }];
  };

  const handleDownloadPlans = (
    visionPlans: SupplementalPlanForEnrollment[] | undefined
  ) => {
    if (visionPlans) {
      exportSupplementalPlanList(
        visionPlans,
        `${insured.employee.firstName} ${insured.employee.lastName}`
      );
    }
  };

  return (
    <ManualSubmissionStep
      step={ManualSubmissionStepType.VISION}
      submitLabel="Continue"
      onboardingPeriodId={onboardingPeriodId}
      onSubmit={handleStepSubmit}
      isSubmitDisabled={isSubmitting}
      employerId={employerId}
      employeeId={employeeId}
      isDon={isDon}
      withReviewCard
    >
      <PlanElectionForm
        getDefaultPlanElectionFormFields={() =>
          getEmptySupplementalPlanFormFields(submittedWhoIsEnrolledOptions)
        }
        mapDataToPlanElectionItem={mapSupplementalPlanToPlanElectionItem}
        whoIsEnrolledOptions={submittedWhoIsEnrolledOptions}
        whoIsEnrolledInMultiplePlans={whoIsEnrolledInMultiplePlans}
        addPlanButtonText="Add another vision plan"
        insuredIdToNameMap={insuredIdToNameMap}
        onDownloadPlans={handleDownloadPlans}
        planElectionForm={planElectionForm}
        getParams={handleGetParams}
        method={visionPlanMethod}
        benefitType={benefitType}
        icon={IconEyeglass}
        title="Vision plan"
      />
    </ManualSubmissionStep>
  );
};
